import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import ScreensPage from "./pages/ScreensPage";
import CampaignsPage from "./pages/CampaignsPage";
import CampaignReportPage from "./pages/CampaignReportPage";
import ContentPage from "./pages/ContentPage";
import ContentAssignmentPage from "./pages/ContentAssignmentPage";
import ContentApprovalPage from "./pages/ContentApprovalPage";
import ScreenConnectionPage from "./pages/ScreenConnectionPage";
import SchedulePage from "./pages/SchedulePage";
import AdvancedSchedulingPage from "./pages/AdvancedSchedulingPage";
import BillingPage from "./pages/BillingPage";
import UsageBillingPage from "./pages/UsageBillingPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import TemplatesPage from "./pages/TemplatesPage";
import RecommendationsPage from "./pages/RecommendationsPage";
import ProgrammaticPage from "./pages/ProgrammaticPage";
import DSPPartnersPage from "./pages/DSPPartnersPage";
import WhiteLabelPage from "./pages/WhiteLabelPage";
import RTBAnalyticsPage from "./pages/RTBAnalyticsPage";
import AudienceAnalyticsPage from "./pages/AudienceAnalyticsPage";
import UsersPage from "./pages/UsersPage";
import AuditLogsPage from "./pages/AuditLogsPage";
import ClientsPage from "./pages/ClientsPage";
import DV360Page from "./pages/DV360Page";
import DeviceCapturePage from "./pages/DeviceCapturePage";
import IntegrationsPage from "./pages/IntegrationsPage";
import ClientDashboardPage from "./pages/ClientDashboardPage";
import ClientBillingPage from "./pages/ClientBillingPage";
import CreativesPage from "./pages/CreativesPage";
import PlayerCampaignsPage from "./pages/PlayerCampaignsPage";
import AdServerPage from "./pages/AdServerPage";
import PlayerReportsPage from "./pages/PlayerReportsPage";
import SettingsPage from "./pages/SettingsPage";
import NotificationsPage from "./pages/NotificationsPage";
import Layout from "./components/Layout";
import "@/App.css";

// Dashboard switcher based on user role
const DashboardSwitcher = () => {
  const { user } = useAuth();
  if (user?.role === 'client') {
    return <ClientDashboardPage />;
  }
  return <DashboardPage />;
};

// Billing switcher based on user role
const BillingSwitcher = () => {
  const { user } = useAuth();
  if (user?.role === 'client') {
    return <ClientBillingPage />;
  }
  return <BillingPage />;
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to="/dashboard" replace /> : <RegisterPage />} />
      
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      <Route element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route path="/dashboard" element={<DashboardSwitcher />} />
        <Route path="/client-dashboard" element={<ClientDashboardPage />} />
        <Route path="/screens" element={<ScreensPage />} />
        <Route path="/screen-connection" element={<ScreenConnectionPage />} />
        <Route path="/campaigns" element={<CampaignsPage />} />
        <Route path="/campaigns/:campaignId/report" element={<CampaignReportPage />} />
        <Route path="/content" element={<ContentPage />} />
        <Route path="/content-assignment" element={
          <ProtectedRoute allowedRoles={['admin', 'operator']}>
            <ContentAssignmentPage />
          </ProtectedRoute>
        } />
        <Route path="/content-approval" element={
          <ProtectedRoute allowedRoles={['admin', 'operator']}>
            <ContentApprovalPage />
          </ProtectedRoute>
        } />
        <Route path="/schedule" element={<SchedulePage />} />
        <Route path="/billing" element={<BillingSwitcher />} />
        <Route path="/client-billing" element={<ClientBillingPage />} />
        <Route path="/usage-billing" element={
          <ProtectedRoute allowedRoles={['admin', 'operator']}>
            <UsageBillingPage />
          </ProtectedRoute>
        } />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/recommendations" element={<RecommendationsPage />} />
        <Route path="/programmatic" element={
          <ProtectedRoute allowedRoles={['admin', 'operator']}>
            <ProgrammaticPage />
          </ProtectedRoute>
        } />
        <Route path="/dsp-partners" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <DSPPartnersPage />
          </ProtectedRoute>
        } />
        <Route path="/dv360" element={
          <ProtectedRoute allowedRoles={['admin', 'operator']}>
            <DV360Page />
          </ProtectedRoute>
        } />
        <Route path="/advanced-scheduling" element={
          <ProtectedRoute allowedRoles={['admin', 'operator']}>
            <AdvancedSchedulingPage />
          </ProtectedRoute>
        } />
        <Route path="/white-label" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <WhiteLabelPage />
          </ProtectedRoute>
        } />
        <Route path="/rtb-analytics" element={
          <ProtectedRoute allowedRoles={['admin', 'operator']}>
            <RTBAnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="/audience-analytics" element={
          <ProtectedRoute allowedRoles={['admin', 'operator']}>
            <AudienceAnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="/device-capture" element={
          <ProtectedRoute allowedRoles={['admin', 'operator']}>
            <DeviceCapturePage />
          </ProtectedRoute>
        } />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/users" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <UsersPage />
          </ProtectedRoute>
        } />
        <Route path="/audit-logs" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AuditLogsPage />
          </ProtectedRoute>
        } />
        <Route path="/clients" element={
          <ProtectedRoute allowedRoles={['admin', 'operator']}>
            <ClientsPage />
          </ProtectedRoute>
        } />
        <Route path="/integrations" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <IntegrationsPage />
          </ProtectedRoute>
        } />
        <Route path="/creatives" element={
          <ProtectedRoute allowedRoles={['admin', 'operator']}>
            <CreativesPage />
          </ProtectedRoute>
        } />
        <Route path="/player-campaigns" element={
          <ProtectedRoute allowedRoles={['admin', 'operator']}>
            <PlayerCampaignsPage />
          </ProtectedRoute>
        } />
        <Route path="/ad-server" element={
          <ProtectedRoute allowedRoles={['admin', 'operator']}>
            <AdServerPage />
          </ProtectedRoute>
        } />
        <Route path="/player-reports" element={
          <ProtectedRoute allowedRoles={['admin', 'operator']}>
            <PlayerReportsPage />
          </ProtectedRoute>
        } />
      </Route>
      
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
